import React, { useState, useEffect } from 'react';
import { useDeposit } from '../../hooks/useDeposit';
import './BankDeposit.css';

const BankSelection = ({ onBankSelect }) => {
  const { getDepositDescription, getDepositSettings, getBankAccounts, loading } = useDeposit();
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const [banksError, setBanksError] = useState(null);
  const [depositDescription, setDepositDescription] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedAccountNumber, setCopiedAccountNumber] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // ✅ NEW: Admin-controlled bank warning banner
  const [warningEnabled, setWarningEnabled] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  // ✅ Fetch admin-controlled deposit settings (bank warning)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const result = await getDepositSettings();
        setWarningEnabled(result.bank_warning_enabled);
        setWarningMessage(result.bank_warning_message);
      } catch (error) {
        console.error('❌ Error fetching deposit settings:', error);
        // Silently fail — warning simply won't show if settings can't be loaded
      }
    };

    fetchSettings();
  }, []);

  // ✅ Fetch admin-managed banks via useDeposit hook
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const data = await getBankAccounts();
        setBanks(data);
      } catch (error) {
        console.error('❌ Error fetching bank accounts:', error);
        setBanksError('Failed to load bank accounts. Please refresh the page.');
      } finally {
        setBanksLoading(false);
      }
    };

    fetchBanks();
  }, []);

  // ✅ Fetch deposit description on component mount
  useEffect(() => {
    const fetchDescription = async () => {
      try {
        const result = await getDepositDescription();
        setDepositDescription(result.deposit_description);
      } catch (error) {
        console.error('❌ Error fetching deposit description:', error);
        setFetchError('Failed to load deposit description. Please refresh the page.');
      }
    };

    fetchDescription();
  }, []);

  const handleCopyDescription = async () => {
    try {
      await navigator.clipboard.writeText(depositDescription);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      alert('Failed to copy. Please copy manually.');
    }
  };

  const handleCopyAccountNumber = async (accountNumber, e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(accountNumber);
      setCopiedAccountNumber(accountNumber);
      setTimeout(() => setCopiedAccountNumber(null), 2000);
    } catch (error) {
      alert('Failed to copy. Please copy manually.');
    }
  };

  return (
    <div className="bank-selection">
      <div className="section-header">
        <h2>Select Bank Account</h2>
      </div>

      {/* ✅ Admin-controlled bank warning banner — toggled & edited from Django admin */}
      {warningEnabled && warningMessage && (
        <div className="opay-warning">
          {warningMessage}
        </div>
      )}

      {/* ✅ Banks list — loaded dynamically from admin */}
      <div className="banks-list">
        {banksLoading && (
          <div className="description-loading">
            <div className="spinner"></div>
            <p>Loading bank accounts...</p>
          </div>
        )}

        {banksError && (
          <div className="description-error">
            <p>⚠️ {banksError}</p>
          </div>
        )}

        {!banksLoading && !banksError && banks.length === 0 && (
          <div className="description-error">
            <p>⚠️ No bank accounts available at the moment. Please contact support.</p>
          </div>
        )}

        {!banksLoading && !banksError && banks.map((bank) => (
          <div
            key={bank.id}
            className="bank-card"
            onClick={() => onBankSelect({
              id: bank.id,
              name: bank.bank_name,
              accountName: bank.account_name,
              accountNumber: bank.account_number,
              logo: bank.logo_url,
            })}
          >
            <div className="bank-logo">
              {bank.logo_url ? (
                <img
                  src={bank.logo_url}
                  alt={bank.bank_name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div class="bank-initial">${bank.bank_name.charAt(0)}</div>`;
                  }}
                />
              ) : (
                <div className="bank-initial">{bank.bank_name.charAt(0)}</div>
              )}
            </div>
            <div className="bank-info">
              <h3 className="bank-name">{bank.bank_name}</h3>
              <p className="account-name">{bank.account_name}</p>
              <div className="account-number-wrapper">
                <p className="account-number">{bank.account_number}</p>
                <button
                  className={`copy-account-btn ${copiedAccountNumber === bank.account_number ? 'copied' : ''}`}
                  onClick={(e) => handleCopyAccountNumber(bank.account_number, e)}
                  title="Copy account number"
                  type="button"
                >
                  {copiedAccountNumber === bank.account_number ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="bank-arrow">→</div>
          </div>
        ))}
      </div>

      {/* Deposit Description Section */}
      <div className="deposit-description-section">
        <h3 className="description-title">Description</h3>
        <p className="description-info">
          Use this description when making your bank transfer. This helps us identify your payment quickly.
        </p>

        {loading && (
          <div className="description-loading">
            <div className="spinner"></div>
            <p>Loading your description...</p>
          </div>
        )}

        {fetchError && (
          <div className="description-error">
            <p>⚠️ {fetchError}</p>
          </div>
        )}

        {!loading && !fetchError && depositDescription && (
          <div className="description-container">
            <div className="description-box">
              <code className="description-text">{depositDescription}</code>
              <p className="description-minimum">Minimum amount: ₦2,000</p>
            </div>
            <button
              className={`copy-button ${copied ? 'copied' : ''}`}
              onClick={handleCopyDescription}
              type="button"
            >
              {copied ? (
                <><span className="checkmark">✓</span> Copied!</>
              ) : (
                <><span className="copy-icon">📋</span> Copy Description</>
              )}
            </button>
          </div>
        )}

        <div className="description-instructions">
          <h4>How to use:</h4>
          <ol>
            <li>Copy the description above</li>
            <li>Select a bank account from the list</li>
            <li>Make transfer in your banking app</li>
            <li>Paste this description in the transfer narration/description field</li>
            <li>Complete your transaction in our app</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default BankSelection;